import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    ArrowLeft, Building2, Globe, Mail, Phone, User,
    MessageSquare, Calendar, FileText, Ticket, Bot, Send,
    Edit3, Save, X, Check, RefreshCw, ExternalLink, Paperclip,
    Plus, Trash2, Clock, Video,
    Download, Image as ImageIcon,
    Zap, Briefcase, ChevronLeft, ChevronRight, Users, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const STATUS_OPTIONS = [
    { value: 'contract_signed', label: '🟢 კონტრაქტი გაფორმდა (მწვანე)', category: 'green', color: 'success', border: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)', text: '#15803d' },
    { value: 'supported_live', label: '🟢 ლაივ & მხარდაჭერა (მწვანე)', category: 'green', color: 'success', border: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)', text: '#15803d' },
    { value: 'in_development', label: '🟡 დამუშავებაში (ყვითელი)', category: 'yellow', color: 'warning', border: '#eab308', bg: 'rgba(234, 179, 8, 0.2)', text: '#854d0e' },
    { value: 'meeting_scheduled', label: '🟡 შეხვედრა დაგეგმილია (ყვითელი)', category: 'yellow', color: 'warning', border: '#eab308', bg: 'rgba(234, 179, 8, 0.2)', text: '#854d0e' },
    { value: 'negotiation', label: '🟡 მოლაპარაკება (ყვითელი)', category: 'yellow', color: 'warning', border: '#eab308', bg: 'rgba(234, 179, 8, 0.2)', text: '#854d0e' },
    { value: 'offer_sent', label: '🔵 შეთავაზება გაგზავნილია (ლურჯი)', category: 'blue', color: 'info', border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', text: '#1e40af' },
    { value: 'lead', label: '🔴 ლიდი / კომუნიკაციის გარეშე (წითელი)', category: 'red', color: 'danger', border: '#f87171', bg: 'rgba(239, 68, 68, 0.15)', text: '#b91c1c' },
    { value: 'rejected', label: '🔴 უარყოფილია (წითელი)', category: 'red', color: 'danger', border: '#f87171', bg: 'rgba(239, 68, 68, 0.15)', text: '#b91c1c' },
];

const OFFER_TYPES = [
    { value: 'create_new', label: 'ახალი ვებსაიტის შექმნა' },
    { value: 'redesign_update', label: 'ვებსაიტის რედიზაინი' },
    { value: 'maintenance_support', label: 'ტექნიკური მხარდაჭერა' },
    { value: 'cybersecurity_audit', label: 'კიბერ-უსაფრთხოების აუდიტი' },
    { value: 'all_in_one', label: 'სრული ციფრული პაკეტი' },
];

const EVENT_TYPES = [
    { value: 'meeting', label: 'ონლაინ შეხვედრა', icon: <Video size={14} /> },
    { value: 'follow_up', label: 'Follow-up', icon: <Phone size={14} /> },
    { value: 'offer_deadline', label: 'შეთავაზების ვადა', icon: <Clock size={14} /> },
    { value: 'demo', label: 'დემო პრეზენტაცია', icon: <Briefcase size={14} /> },
    { value: 'milestone', label: 'მაილსთოუნი', icon: <Check size={14} /> },
];

const AdminMunicipalityDetail = () => {
    const { id } = useParams();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    useTranslation();
    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const [municipality, setMunicipality] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('chat');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [chatMessage, setChatMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isGeneratingOffer, setIsGeneratingOffer] = useState(false);
    const [allMunicipalities, setAllMunicipalities] = useState([]);
    const [prevHover, setPrevHover] = useState(false);
    const [nextHover, setNextHover] = useState(false);

    // Event form
    const [showEventForm, setShowEventForm] = useState(false);
    const [eventForm, setEventForm] = useState({
        title: '', event_date: '', event_type: 'meeting',
        meeting_link: '', description: ''
    });

    // Contact form & selected PDF state
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactForm, setContactForm] = useState({
        first_name: '', last_name: '', position: '',
        phone: '', email: '', notes: ''
    });
    const [selectedPdf, setSelectedPdf] = useState(null);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php?id=${id}`);
            const data = await res.json();
            if (data && !data.error) {
                setMunicipality(data);
                setEditForm({
                    name_ka: data.name_ka || '',
                    official_website: data.official_website || '',
                    contact_email: data.contact_email || '',
                    contact_phone: data.contact_phone || '',
                    contact_person: data.contact_person || '',
                    contact_position: data.contact_position || '',
                    offer_type: data.offer_type || 'redesign_update',
                    status: data.status || 'lead',
                    estimated_budget: data.estimated_budget || 0,
                    meeting_link: data.meeting_link || '',
                    autonomous_mode: data.autonomous_mode ?? 1,
                    notes: data.notes || '',
                });
            } else {
                toast.error('მუნიციპალიტეტი ვერ მოიძებნა');
                navigate('/admin/municipalities');
            }
        } catch (err) {
            toast.error('ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [id, navigate]);

    useEffect(() => {
        if (user) fetchData();
    }, [user, fetchData]);

    // Fetch the list of all municipalities for prev/next
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const res = await fetchWithSecurity(`${API_URL}municipalities.php`);
                const data = await res.json();
                if (Array.isArray(data)) {
                    setAllMunicipalities(data.map(m => ({ id: m.id, name_ka: m.name_ka, logo: m.logo })));
                }
            } catch {}
        };
        if (user) fetchAll();
    }, [user]);

    useEffect(() => {
        if (chatEndRef.current && activeTab === 'chat') {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [municipality?.communications, activeTab]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: parseInt(id), ...editForm })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('ცვლილებები შენახულია!');
                setEditing(false);
                fetchData();
            } else {
                toast.error(data.error || 'შენახვა ვერ მოხერხდა');
            }
        } catch (err) {
            toast.error('შენახვა ვერ მოხერხდა');
        }
        setSaving(false);
    };

    const handleSendMessage = async () => {
        if (!chatMessage.trim()) return;
        setIsSending(true);
        const msg = chatMessage.trim();
        setChatMessage('');
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_communication',
                    municipality_id: parseInt(id),
                    sender_type: 'agency',
                    sender_name: user?.username || 'Orbitronix Agency',
                    channel: 'portal_chat',
                    message: msg
                })
            });
            const data = await res.json();
            if (data.success) {
                fetchData();
            } else {
                toast.error('შეტყობინება ვერ გაიგზავნა');
            }
        } catch (err) {
            toast.error('შეტყობინება ვერ გაიგზავნა');
        }
        setIsSending(false);
    };

    const handleGenerateOffer = async () => {
        setIsGeneratingOffer(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'generate_autonomous_offer',
                    municipality_id: parseInt(id),
                    offer_type: editForm.offer_type,
                    estimated_budget: editForm.estimated_budget
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('AI შეთავაზება წარმატებით გენერირებულია!');
                setActiveTab('chat');
                fetchData();
            } else {
                toast.error(data.error || 'შეთავაზება ვერ გენერირდა');
            }
        } catch (err) {
            toast.error('AI შეთავაზება ვერ გენერირდა');
        }
        setIsGeneratingOffer(false);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('municipality_id', parseInt(id));
        formData.append('uploaded_by', user?.username || 'Agency Staff');
        try {
            const uploadRes = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'POST',
                body: formData
            });
            const uploadData = await uploadRes.json();
            if (uploadData.success) {
                toast.success('ფაილი წარმატებით ატვირთულია!');
                fetchData();
            } else {
                toast.error(uploadData.error || 'ფაილის ატვირთვა ვერ მოხერხდა');
            }
        } catch (err) {
            toast.error('ფაილის ატვირთვა ვერ მოხერხდა');
        }
        e.target.value = '';
    };

    const handleAddContact = async (e) => {
        e.preventDefault();
        if (!contactForm.first_name.trim() || !contactForm.last_name.trim()) {
            toast.error('სახელი და გვარი სავალდებულოა');
            return;
        }
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_contact',
                    municipality_id: parseInt(id),
                    ...contactForm
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('საკონტაქტო პირი წარმატებით დაემატა!');
                setShowContactForm(false);
                setContactForm({ first_name: '', last_name: '', position: '', phone: '', email: '', notes: '' });
                fetchData();
            } else {
                toast.error(data.error || 'კონტაქტი ვერ დაემატა');
            }
        } catch (err) {
            toast.error('კონტაქტი ვერ დაემატა');
        }
    };

    const handleDeleteContact = async (contactId) => {
        if (!window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ კონტაქტის წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_contact',
                    contact_id: contactId
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('კონტაქტი წაიშალა');
                fetchData();
            } else {
                toast.error(data.error || 'წაშლა ვერ მოხერხდა');
            }
        } catch (err) {
            toast.error('წაშლა ვერ მოხერხდა');
        }
    };

    const handleAddEvent = async () => {
        if (!eventForm.title || !eventForm.event_date) {
            toast.error('სახელი და თარიღი სავალდებულოა');
            return;
        }
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_event',
                    municipality_id: parseInt(id),
                    ...eventForm
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('ღონისძიება დაემატა!');
                setShowEventForm(false);
                setEventForm({ title: '', event_date: '', event_type: 'meeting', meeting_link: '', description: '' });
                fetchData();
            } else {
                toast.error(data.error || 'ვერ დაემატა');
            }
        } catch (err) {
            toast.error('ვერ დაემატა');
        }
    };

    if (authLoading || !user) return null;

    const statusCfg = STATUS_OPTIONS.find(s => s.value === municipality?.status) || STATUS_OPTIONS[0];
    const communications = municipality?.communications || [];
    const files = municipality?.files || [];
    const events = municipality?.events || [];
    const tickets = municipality?.tickets || [];
    const contacts = municipality?.contacts || [];

    const TABS = [
        { key: 'chat', label: 'ჩატი', icon: <MessageSquare size={16} />, count: communications.length },
        { key: 'contacts', label: 'კონტაქტები', icon: <Users size={16} />, count: contacts.length },
        { key: 'calendar', label: 'კალენდარი', icon: <Calendar size={16} />, count: events.filter(e => e.status === 'scheduled').length },
        { key: 'files', label: 'ფაილები', icon: <FileText size={16} />, count: files.length },
        { key: 'tickets', label: 'ტიკეტები', icon: <Ticket size={16} />, count: tickets.length },
        { key: 'details', label: 'დეტალები', icon: <Edit3 size={16} />, count: null },
    ];

    return (
        <AdminLayout title={municipality?.name_ka || 'მუნიციპალიტეტი'}>
            {/* Back + Prev/Next Buttons */}
            <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
                <button
                    className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                    onClick={() => navigate('/admin/municipalities')}
                >
                    <ArrowLeft size={16} /> მუნიციპალიტეტები
                </button>

                {/* Prev/Next navigation */}
                {allMunicipalities.length > 1 && (() => {
                    const currentIndex = allMunicipalities.findIndex(m => String(m.id) === String(id));
                    const prevMun = currentIndex > 0 ? allMunicipalities[currentIndex - 1] : null;
                    const nextMun = currentIndex < allMunicipalities.length - 1 ? allMunicipalities[currentIndex + 1] : null;
                    return (
                        <div className="d-flex gap-1">
                            {/* Prev */}
                            <div className="position-relative">
                                <button
                                    className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                    disabled={!prevMun}
                                    onClick={() => prevMun && navigate(`/admin/municipalities/${prevMun.id}`)}
                                    onMouseEnter={() => setPrevHover(true)}
                                    onMouseLeave={() => setPrevHover(false)}
                                    title={prevMun?.name_ka || ''}
                                    style={{ opacity: prevMun ? 1 : 0.4 }}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                {prevHover && prevMun && (
                                    <div style={{
                                        position: 'absolute', top: '110%', left: 0, zIndex: 500,
                                        background: '#fff', border: '1px solid var(--admin-border)',
                                        borderRadius: '12px', padding: '10px 14px', minWidth: '150px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: '10px'
                                    }}>
                                        {prevMun.logo
                                            ? <img src={prevMun.logo} alt="" style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                            : <span style={{ fontSize: '28px' }}>🏛️</span>
                                        }
                                        <span className="fw-semibold text-dark small">{prevMun.name_ka}</span>
                                    </div>
                                )}
                            </div>
                            {/* Next */}
                            <div className="position-relative">
                                <button
                                    className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                    disabled={!nextMun}
                                    onClick={() => nextMun && navigate(`/admin/municipalities/${nextMun.id}`)}
                                    onMouseEnter={() => setNextHover(true)}
                                    onMouseLeave={() => setNextHover(false)}
                                    title={nextMun?.name_ka || ''}
                                    style={{ opacity: nextMun ? 1 : 0.4 }}
                                >
                                    <ChevronRight size={16} />
                                </button>
                                {nextHover && nextMun && (
                                    <div style={{
                                        position: 'absolute', top: '110%', right: 0, zIndex: 500,
                                        background: '#fff', border: '1px solid var(--admin-border)',
                                        borderRadius: '12px', padding: '10px 14px', minWidth: '150px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: '10px'
                                    }}>
                                        {nextMun.logo
                                            ? <img src={nextMun.logo} alt="" style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                            : <span style={{ fontSize: '28px' }}>🏛️</span>
                                        }
                                        <span className="fw-semibold text-dark small">{nextMun.name_ka}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
                {municipality && (
                    <span 
                        className="badge rounded-pill px-3 py-1 fs-7 fw-semibold"
                        style={{
                            background: statusCfg.bg,
                            color: statusCfg.text,
                            border: `1.5px solid ${statusCfg.border}`
                        }}
                    >
                        {statusCfg.label}
                    </span>
                )}
                {municipality?.autonomous_mode === 1 && (
                    <span className="shadcn-badge shadcn-badge-info d-flex align-items-center gap-1">
                        <Bot size={12} /> AI Auto
                    </span>
                )}
                <div className="ms-auto d-flex gap-2">
                    <button
                        className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                        onClick={fetchData}
                    >
                        <RefreshCw size={14} /> განახლება
                    </button>
                    {!editing ? (
                        <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => setEditing(true)}>
                            <Edit3 size={14} /> რედაქტირება
                        </button>
                    ) : (
                        <>
                            <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setEditing(false)}>
                                <X size={14} /> გაუქმება
                            </button>
                            <button className="shadcn-btn shadcn-btn-success shadcn-btn-sm" onClick={handleSave} disabled={saving}>
                                <Save size={14} /> {saving ? 'ინახება...' : 'შენახვა'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
            ) : !municipality ? null : (
                <div className="row g-4">
                    {/* ── LEFT: PROFILE CARD ── */}
                    <div className="col-lg-4">
                        <div className="shadcn-card mb-4">
                            {/* Logo & Name */}
                            <div className="text-center mb-4">
                                <div style={{
                                    width: '90px', height: '90px', borderRadius: '16px', overflow: 'hidden',
                                    border: '3px solid #e5e7eb', margin: '0 auto 12px', background: '#f8fafc',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {municipality.logo ? (
                                        <img
                                            src={municipality.logo}
                                            alt={municipality.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="font-size:36px">🏛️</span>'; }}
                                        />
                                    ) : <span style={{ fontSize: '36px' }}>🏛️</span>}
                                </div>
                                <h5 className="fw-bold text-dark mb-0">{municipality.name_ka || municipality.name}</h5>
                                <div className="text-muted small">{municipality.name}</div>
                                <div className="text-muted smallest">{municipality.region_ka || municipality.region}</div>
                                {municipality.is_city === 1 && (
                                    <span className="shadcn-badge shadcn-badge-primary mt-1">თვითმმართველი ქალაქი</span>
                                )}
                            </div>

                            {/* Status & Offer Type selector */}
                            {editing ? (
                                <div className="d-flex flex-column gap-3 mb-3">
                                    <div>
                                        <label className="form-group-label">სტატუსი</label>
                                        <select className="shadcn-input" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                                            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-group-label">შეთავაზების ტიპი</label>
                                        <select className="shadcn-input" value={editForm.offer_type} onChange={e => setEditForm({ ...editForm, offer_type: e.target.value })}>
                                            {OFFER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-group-label">სავარაუდო ბიუჯეტი (GEL)</label>
                                        <input type="number" className="shadcn-input" value={editForm.estimated_budget} onChange={e => setEditForm({ ...editForm, estimated_budget: e.target.value })} />
                                    </div>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2 mb-3">
                                    <div className="d-flex justify-content-between p-2 rounded-3 bg-light">
                                        <span className="text-muted small">შეთავაზება</span>
                                        <span className="small fw-semibold">{OFFER_TYPES.find(o => o.value === municipality.offer_type)?.label || municipality.offer_type}</span>
                                    </div>
                                    <div className="d-flex justify-content-between p-2 rounded-3 bg-light">
                                        <span className="text-muted small">ბიუჯეტი</span>
                                        <span className="small fw-bold text-success">₾{parseFloat(municipality.estimated_budget || 0).toLocaleString()}</span>
                                    </div>
                                </div>
                            )}

                            {/* Contact Info */}
                            <div className="border-top pt-3">
                                <div className="small fw-bold text-dark mb-2">საკონტაქტო ინფო</div>
                                {editing ? (
                                    <div className="d-flex flex-column gap-2">
                                        {[
                                            { key: 'contact_person', label: 'კონტაქტი', placeholder: 'სახელი გვარი' },
                                            { key: 'contact_position', label: 'თანამდებობა', placeholder: 'დეპარტამენტი...' },
                                            { key: 'contact_email', label: 'ელ.ფოსტა', placeholder: 'info@...' },
                                            { key: 'contact_phone', label: 'ტელეფონი', placeholder: '+995...' },
                                            { key: 'official_website', label: 'ვებსაიტი', placeholder: 'https://...' },
                                            { key: 'meeting_link', label: 'Meet ბმული', placeholder: 'https://meet.google.com/...' },
                                        ].map(field => (
                                            <div key={field.key}>
                                                <label className="form-group-label">{field.label}</label>
                                                <input
                                                    type="text"
                                                    className="shadcn-input"
                                                    placeholder={field.placeholder}
                                                    value={editForm[field.key] || ''}
                                                    onChange={e => setEditForm({ ...editForm, [field.key]: e.target.value })}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-1">
                                        {municipality.contact_person && (
                                            <div className="d-flex align-items-center gap-2 small text-dark">
                                                <User size={13} className="text-primary flex-shrink-0" />
                                                <span>{municipality.contact_person}</span>
                                            </div>
                                        )}
                                        {municipality.contact_position && (
                                            <div className="text-muted smallest ms-3 ps-1">{municipality.contact_position}</div>
                                        )}
                                        {municipality.contact_email && (
                                            <a href={`mailto:${municipality.contact_email}`} className="d-flex align-items-center gap-2 small text-dark text-decoration-none">
                                                <Mail size={13} className="text-info flex-shrink-0" />
                                                <span>{municipality.contact_email}</span>
                                            </a>
                                        )}
                                        {municipality.contact_phone && (
                                            <a href={`tel:${municipality.contact_phone}`} className="d-flex align-items-center gap-2 small text-dark text-decoration-none">
                                                <Phone size={13} className="text-success flex-shrink-0" />
                                                <span>{municipality.contact_phone}</span>
                                            </a>
                                        )}
                                        {municipality.official_website && (
                                            <a href={municipality.official_website} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 small text-primary text-decoration-none">
                                                <Globe size={13} className="flex-shrink-0" />
                                                <span>ოფიციალური ვებსაიტი</span>
                                                <ExternalLink size={11} />
                                            </a>
                                        )}
                                        {municipality.meeting_link && (
                                            <a href={municipality.meeting_link} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 small text-info text-decoration-none">
                                                <Video size={13} className="flex-shrink-0" />
                                                <span>Google Meet</span>
                                                <ExternalLink size={11} />
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* AI Autonomous Action Card */}
                        <div className="shadcn-card" style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', border: 'none' }}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Bot size={20} className="text-white" />
                                <span className="fw-bold text-white small">AI ავტო-შეთავაზება</span>
                            </div>
                            <p className="text-white text-opacity-75 smallest mb-3">
                                AI ავტომატურად ამზადებს ოფიციალური ტექნიკური შეთავაზების ტექსტს და ათავსებს კომუნიკაციის ჰისტორიაში.
                            </p>
                            <div className="mb-2">
                                <select
                                    className="shadcn-input"
                                    value={editForm.offer_type}
                                    onChange={e => setEditForm({ ...editForm, offer_type: e.target.value })}
                                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                                >
                                    {OFFER_TYPES.map(o => <option key={o.value} value={o.value} style={{ color: '#000' }}>{o.label}</option>)}
                                </select>
                            </div>
                            <button
                                className="shadcn-btn w-100 mt-1"
                                style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)', color: '#fff', border: 'none' }}
                                onClick={handleGenerateOffer}
                                disabled={isGeneratingOffer}
                            >
                                {isGeneratingOffer ? (
                                    <><div className="spinner-border spinner-border-sm me-2"></div>AI მუშაობს...</>
                                ) : (
                                    <><Zap size={16} /> შეთავაზების გენერირება</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* ── RIGHT: TABS ── */}
                    <div className="col-lg-8">
                        {/* Tab Navigation */}
                        <div className="d-flex gap-1 mb-3 flex-wrap">
                            {TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    className={`shadcn-btn shadcn-btn-sm ${activeTab === tab.key ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.icon}
                                    <span className="d-none d-md-inline ms-1">{tab.label}</span>
                                    {tab.count !== null && tab.count > 0 && (
                                        <span className={`ms-1 badge rounded-pill ${activeTab === tab.key ? 'bg-white text-primary' : 'bg-primary text-white'}`} style={{ fontSize: '10px' }}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* ── CHAT TAB ── */}
                        {activeTab === 'chat' && (
                            <div className="shadcn-card d-flex flex-column" style={{ height: '600px' }}>
                                <div className="shadcn-card-header">
                                    <div className="shadcn-card-title">
                                        <MessageSquare size={18} className="text-primary" />
                                        <span>კომუნიკაციის ისტორია</span>
                                    </div>
                                    <span className="shadcn-badge shadcn-badge-secondary">{communications.length} შეტყობინება</span>
                                </div>

                                {/* Messages */}
                                <div className="flex-grow-1 overflow-auto p-3 d-flex flex-column gap-3" style={{ background: '#f8fafc', borderRadius: '8px', margin: '0 16px' }}>
                                    {communications.length === 0 ? (
                                        <div className="text-center text-muted py-5">
                                            <MessageSquare size={36} className="mb-2 opacity-25" />
                                            <p className="small">კომუნიკაცია ჯერ არ დაწყებულა</p>
                                        </div>
                                    ) : communications.map(comm => {
                                        const isAgency = comm.sender_type === 'agency';
                                        const isAI = comm.sender_type === 'autonomous_ai';
                                        const isMuni = comm.sender_type === 'municipality';

                                        return (
                                            <div
                                                key={comm.id}
                                                className={`d-flex ${isAgency || isAI ? 'justify-content-end' : 'justify-content-start'}`}
                                            >
                                                <div style={{ maxWidth: '78%' }}>
                                                    <div className={`d-flex align-items-center gap-1 mb-1 ${isAgency || isAI ? 'justify-content-end' : ''}`}>
                                                        {isAI && <Bot size={12} className="text-indigo" />}
                                                        <span className="text-muted" style={{ fontSize: '11px' }}>
                                                            {comm.sender_name} • {new Date(comm.created_at).toLocaleString('ka-GE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="p-3 rounded-3 shadow-sm"
                                                        style={{
                                                            background: isAI
                                                                ? 'linear-gradient(135deg, #1e1b4b, #312e81)'
                                                                : isAgency
                                                                    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                                                                    : '#fff',
                                                            color: isAgency || isAI ? '#fff' : '#1e293b',
                                                            borderRadius: isAgency || isAI ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                            border: isMuni ? '1px solid #e5e7eb' : 'none',
                                                            fontSize: '14px',
                                                            whiteSpace: 'pre-wrap'
                                                        }}
                                                    >
                                                        {isAI && (
                                                            <div className="d-flex align-items-center gap-1 mb-2 opacity-75">
                                                                <Bot size={12} />
                                                                <span style={{ fontSize: '11px', fontWeight: 600 }}>AI Generated Proposal</span>
                                                            </div>
                                                        )}
                                                        {comm.message}
                                                        {comm.attachment_url && (
                                                            <div className="mt-2 pt-2 border-top border-white border-opacity-25">
                                                                <a href={comm.attachment_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-1 text-white-50 text-decoration-none smallest">
                                                                    <Paperclip size={11} /> დანართი
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={chatEndRef} />
                                </div>

                                {/* Message Input */}
                                <div className="p-3 border-top d-flex gap-2 align-items-end">
                                    <div className="flex-grow-1">
                                        <textarea
                                            className="shadcn-textarea"
                                            rows={2}
                                            placeholder="დაწერეთ შეტყობინება მუნიციპალიტეტს... (Enter = ახალი სტრიქონი)"
                                            value={chatMessage}
                                            onChange={e => setChatMessage(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && e.ctrlKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <label className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm cursor-pointer mb-0">
                                            <Paperclip size={14} />
                                            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                                        </label>
                                        <button
                                            className="shadcn-btn shadcn-btn-primary shadcn-btn-sm"
                                            onClick={handleSendMessage}
                                            disabled={isSending || !chatMessage.trim()}
                                        >
                                            {isSending ? <div className="spinner-border spinner-border-sm"></div> : <Send size={14} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── CALENDAR TAB ── */}
                        {activeTab === 'calendar' && (
                            <div className="shadcn-card">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                                    <div className="shadcn-card-title">
                                        <Calendar size={18} className="text-primary" />
                                        <span>ღონისძიებები & შეხვედრები</span>
                                    </div>
                                    <button
                                        className="shadcn-btn shadcn-btn-primary shadcn-btn-sm"
                                        onClick={() => setShowEventForm(!showEventForm)}
                                    >
                                        <Plus size={14} /> ახალი ღონ.
                                    </button>
                                </div>

                                {/* New Event Form */}
                                {showEventForm && (
                                    <div className="p-4 rounded-3 bg-light border mb-4">
                                        <div className="fw-bold small text-dark mb-3">ახალი ღონისძიება</div>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-group-label">სახელი *</label>
                                                <input type="text" className="shadcn-input" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} placeholder="შეხვედრის სათაური..." />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-group-label">ტიპი</label>
                                                <select className="shadcn-input" value={eventForm.event_type} onChange={e => setEventForm({ ...eventForm, event_type: e.target.value })}>
                                                    {EVENT_TYPES.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-group-label">თარიღი & დრო *</label>
                                                <input type="datetime-local" className="shadcn-input" value={eventForm.event_date} onChange={e => setEventForm({ ...eventForm, event_date: e.target.value })} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-group-label">Meeting ბმული</label>
                                                <input type="url" className="shadcn-input" value={eventForm.meeting_link} onChange={e => setEventForm({ ...eventForm, meeting_link: e.target.value })} placeholder="https://meet.google.com/..." />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-group-label">შენიშვნა</label>
                                                <textarea className="shadcn-textarea" rows={2} value={eventForm.description} onChange={e => setEventForm({ ...eventForm, description: e.target.value })} placeholder="ღონისძიების აღწერა..." />
                                            </div>
                                            <div className="col-12 d-flex gap-2">
                                                <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={handleAddEvent}>
                                                    <Check size={14} /> დამატება
                                                </button>
                                                <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setShowEventForm(false)}>
                                                    <X size={14} /> გაუქმება
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Events List */}
                                {events.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <Calendar size={36} className="mb-2 opacity-25" />
                                        <p className="small">ღონისძიებები არ არის</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {events.map(ev => {
                                            const evType = EVENT_TYPES.find(et => et.value === ev.event_type);
                                            const isPast = new Date(ev.event_date) < new Date();
                                            return (
                                                <div key={ev.id}
                                                    className={`d-flex gap-3 p-3 rounded-3 border ${isPast ? 'opacity-60 bg-light' : 'bg-white'}`}
                                                    style={{ opacity: isPast ? 0.65 : 1 }}
                                                >
                                                    <div className="d-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10 text-primary flex-shrink-0"
                                                        style={{ width: '40px', height: '40px' }}>
                                                        {evType?.icon || <Calendar size={16} />}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-bold small text-dark">{ev.title}</div>
                                                        <div className="text-muted smallest">
                                                            {new Date(ev.event_date).toLocaleString('ka-GE')}
                                                        </div>
                                                        {ev.description && <div className="text-muted smallest mt-1">{ev.description}</div>}
                                                    </div>
                                                    <div className="d-flex align-items-start gap-2">
                                                        {ev.meeting_link && (
                                                            <a href={ev.meeting_link} target="_blank" rel="noopener noreferrer"
                                                                className="shadcn-btn shadcn-btn-primary shadcn-btn-sm">
                                                                <Video size={14} /> შეხვედრა
                                                            </a>
                                                        )}
                                                        <span className={`shadcn-badge shadcn-badge-${isPast ? 'secondary' : ev.status === 'scheduled' ? 'success' : 'secondary'}`}>
                                                            {isPast ? 'დასრულდა' : 'დაგეგმილია'}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── CONTACTS TAB ── */}
                        {activeTab === 'contacts' && (
                            <div className="shadcn-card">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                                    <div className="shadcn-card-title d-flex align-items-center gap-2">
                                        <Users size={18} className="text-primary" />
                                        <span>მუნიციპალიტეტის საკონტაქტო პირები ({contacts.length})</span>
                                    </div>
                                    <button 
                                        className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" 
                                        onClick={() => setShowContactForm(!showContactForm)}
                                    >
                                        <Plus size={14} /> {showContactForm ? 'დახურვა' : 'ახალი კონტაქტი'}
                                    </button>
                                </div>

                                {/* Add Contact Form */}
                                {showContactForm && (
                                    <div className="p-3 mb-4 rounded-3 border bg-light m-3">
                                        <h6 className="fw-bold mb-3 small text-dark">ახალი საკონტაქტო პირის დამატება</h6>
                                        <form onSubmit={handleAddContact}>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label className="form-group-label smallest">სახელი *</label>
                                                    <input 
                                                        type="text" 
                                                        className="shadcn-input" 
                                                        value={contactForm.first_name} 
                                                        onChange={e => setContactForm({ ...contactForm, first_name: e.target.value })} 
                                                        placeholder="გიორგი" 
                                                        required 
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-group-label smallest">გვარი *</label>
                                                    <input 
                                                        type="text" 
                                                        className="shadcn-input" 
                                                        value={contactForm.last_name} 
                                                        onChange={e => setContactForm({ ...contactForm, last_name: e.target.value })} 
                                                        placeholder="ბერიძე" 
                                                        required 
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-group-label smallest">თანამდებობა</label>
                                                    <input 
                                                        type="text" 
                                                        className="shadcn-input" 
                                                        value={contactForm.position} 
                                                        onChange={e => setContactForm({ ...contactForm, position: e.target.value })} 
                                                        placeholder="მაგ. მერის მოადგილე, IT ხელმძღვანელი" 
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-group-label smallest">ტელეფონის ნომერი</label>
                                                    <input 
                                                        type="tel" 
                                                        className="shadcn-input" 
                                                        value={contactForm.phone} 
                                                        onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} 
                                                        placeholder="+995 599 00 00 00" 
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-group-label smallest">ელ. ფოსტა</label>
                                                    <input 
                                                        type="email" 
                                                        className="shadcn-input" 
                                                        value={contactForm.email} 
                                                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })} 
                                                        placeholder="contact@municipality.gov.ge" 
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-group-label smallest">შენიშვნა / დამატებითი ინფო</label>
                                                    <textarea 
                                                        className="shadcn-input" 
                                                        rows="2" 
                                                        value={contactForm.notes} 
                                                        onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} 
                                                        placeholder="სამუშაო საათები, კომუნიკაციის უპირატესი არხი..." 
                                                    />
                                                </div>
                                                <div className="col-12 d-flex justify-content-end gap-2">
                                                    <button type="button" className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setShowContactForm(false)}>
                                                        გაუქმება
                                                    </button>
                                                    <button type="submit" className="shadcn-btn shadcn-btn-primary shadcn-btn-sm">
                                                        <Check size={14} /> შენახვა
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {/* Contacts List */}
                                {contacts.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <Users size={36} className="mb-2 opacity-25" />
                                        <p className="small mb-1">საკონტაქტო პირები ჯერ არ არის დამატებული</p>
                                        <p className="smallest text-muted">დაამატეთ მერი, მოადგილე, IT ან PR მენეჯერი</p>
                                    </div>
                                ) : (
                                    <div className="p-3">
                                        <div className="row g-3">
                                            {contacts.map(c => (
                                                <div className="col-md-6" key={c.id}>
                                                    <div className="p-3 rounded-3 border bg-white h-100 d-flex flex-column justify-content-between shadow-xs">
                                                        <div>
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div 
                                                                        className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center fw-bold small flex-shrink-0"
                                                                        style={{ width: '38px', height: '38px' }}
                                                                    >
                                                                        {c.first_name?.[0]}{c.last_name?.[0]}
                                                                    </div>
                                                                    <div>
                                                                        <div className="fw-bold small text-dark">{c.first_name} {c.last_name}</div>
                                                                        {c.position && <div className="text-muted smallest">{c.position}</div>}
                                                                    </div>
                                                                </div>
                                                                <button 
                                                                    className="btn btn-sm btn-outline-danger border-0 p-1"
                                                                    onClick={() => handleDeleteContact(c.id)}
                                                                    title="წაშლა"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>

                                                            <div className="d-flex flex-column gap-1 my-2 smallest">
                                                                {c.phone && (
                                                                    <div className="d-flex align-items-center gap-2 text-muted">
                                                                        <Phone size={12} className="text-primary flex-shrink-0" />
                                                                        <a href={`tel:${c.phone}`} className="text-decoration-none text-dark fw-semibold">
                                                                            {c.phone}
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                {c.email && (
                                                                    <div className="d-flex align-items-center gap-2 text-muted">
                                                                        <Mail size={12} className="text-primary flex-shrink-0" />
                                                                        <a href={`mailto:${c.email}`} className="text-decoration-none text-dark fw-semibold text-truncate">
                                                                            {c.email}
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                {c.notes && (
                                                                    <div className="p-2 rounded bg-light border smallest text-secondary mt-1">
                                                                        {c.notes}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="d-flex gap-2 pt-2 border-top mt-2">
                                                            {c.phone && (
                                                                <a href={`tel:${c.phone}`} className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm flex-grow-1 text-center text-decoration-none">
                                                                    <Phone size={12} /> დარეკვა
                                                                </a>
                                                            )}
                                                            {c.email && (
                                                                <a href={`mailto:${c.email}`} className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm flex-grow-1 text-center text-decoration-none">
                                                                    <Mail size={12} /> წერილი
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── FILES TAB ── */}
                        {activeTab === 'files' && (
                            <div className="shadcn-card">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                                    <div className="shadcn-card-title">
                                        <FileText size={18} className="text-primary" />
                                        <span>დოკუმენტები & ფაილები</span>
                                    </div>
                                    <label className="shadcn-btn shadcn-btn-primary shadcn-btn-sm cursor-pointer mb-0">
                                        <Plus size={14} /> ფაილის ატვირთვა
                                        <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                                    </label>
                                </div>
                                {files.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <FileText size={36} className="mb-2 opacity-25" />
                                        <p className="small">ფაილები არ არის ატვირთული</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {files.map(file => {
                                            const fileUrl = file.file_url.startsWith('http') ? file.file_url : `${API_URL}${file.file_url}`;
                                            const isPdf = file.file_name.toLowerCase().endsWith('.pdf') || file.file_type === 'pdf';
                                            return (
                                            <div key={file.id} className="d-flex align-items-center gap-3 p-3 rounded-3 border bg-white">
                                                <div className="text-primary flex-shrink-0">
                                                    {file.file_type === 'image' ? <ImageIcon size={22} /> : <FileText size={22} />}
                                                </div>
                                                <div className="flex-grow-1 min-w-0">
                                                    <div className="small fw-semibold text-dark text-truncate">{file.file_name}</div>
                                                    <div className="text-muted smallest">
                                                        {file.uploaded_by} • {new Date(file.created_at).toLocaleDateString('ka-GE')}
                                                        {file.file_size > 0 && ` • ${(file.file_size / 1024).toFixed(1)} KB`}
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2 flex-shrink-0">
                                                    {isPdf && (
                                                        <button 
                                                            className="shadcn-btn shadcn-btn-primary shadcn-btn-sm"
                                                            onClick={() => setSelectedPdf({ url: fileUrl, name: file.file_name, size: file.file_size })}
                                                            title="PDF ნახვა"
                                                        >
                                                            <Eye size={13} /> ნახვა
                                                        </button>
                                                    )}
                                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" download
                                                        className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm">
                                                        <Download size={13} />
                                                    </a>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── TICKETS TAB ── */}
                        {activeTab === 'tickets' && (
                            <div className="shadcn-card">
                                <div className="shadcn-card-header">
                                    <div className="shadcn-card-title">
                                        <Ticket size={18} className="text-primary" />
                                        <span>დაკავშირებული ტიკეტები</span>
                                    </div>
                                </div>
                                {tickets.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <Ticket size={36} className="mb-2 opacity-25" />
                                        <p className="small">ტიკეტები არ არის</p>
                                    </div>
                                ) : (
                                    <div className="shadcn-table-wrapper">
                                        <table className="shadcn-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>სათაური</th>
                                                    <th>სტატუსი</th>
                                                    <th>თარიღი</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tickets.map(tk => (
                                                    <tr key={tk.id}>
                                                        <td className="font-monospace text-muted">#{tk.id}</td>
                                                        <td className="fw-semibold">{tk.subject}</td>
                                                        <td><span className="shadcn-badge shadcn-badge-primary">{tk.status}</span></td>
                                                        <td className="text-muted small">{new Date(tk.created_at).toLocaleDateString()}</td>
                                                        <td>
                                                            <Link to={`/admin/tickets/${tk.id}`} className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm">
                                                                <ExternalLink size={13} />
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── DETAILS TAB ── */}
                        {activeTab === 'details' && (
                            <div className="shadcn-card">
                                <div className="shadcn-card-header">
                                    <div className="shadcn-card-title">
                                        <Building2 size={18} className="text-primary" />
                                        <span>სრული ინფორმაცია</span>
                                    </div>
                                </div>
                                <div className="row g-3">
                                    {[
                                        { label: 'ქართული სახელი', value: municipality.name_ka },
                                        { label: 'ლათინური სახელი', value: municipality.name },
                                        { label: 'რეგიონი', value: municipality.region_ka || municipality.region },
                                        { label: 'სლაგი', value: municipality.slug, mono: true },
                                        { label: 'AI ავტო-რეჟიმი', value: municipality.autonomous_mode === 1 ? '✅ ჩართულია' : '❌ გამორთულია' },
                                        { label: 'AI სტეიჯი', value: municipality.autonomous_stage },
                                    ].map((row, i) => (
                                        <div key={i} className="col-md-6">
                                            <div className="p-3 rounded-3 bg-light">
                                                <div className="text-muted smallest mb-1">{row.label}</div>
                                                <div className={`small fw-semibold ${row.mono ? 'font-monospace' : ''}`}>{row.value || '—'}</div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Notes */}
                                    <div className="col-12">
                                        <div className="p-3 rounded-3 bg-light">
                                            <div className="text-muted smallest mb-1">შენიშვნები</div>
                                            {editing ? (
                                                <textarea
                                                    className="shadcn-textarea mt-1"
                                                    rows={4}
                                                    value={editForm.notes}
                                                    onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                                />
                                            ) : (
                                                <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{municipality.notes || '—'}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {editing && (
                                    <div className="mt-4 d-flex gap-2">
                                        <button className="shadcn-btn shadcn-btn-primary" onClick={handleSave} disabled={saving}>
                                            <Save size={16} /> {saving ? 'ინახება...' : 'ცვლილებების შენახვა'}
                                        </button>
                                        <button className="shadcn-btn shadcn-btn-secondary" onClick={() => setEditing(false)}>
                                            <X size={16} /> გაუქმება
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* ── INLINE PDF VIEWER POPUP MODAL ── */}
            {selectedPdf && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(5px)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '90vw' }}>
                        <div className="modal-content shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header bg-dark text-white border-0 py-3 d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                    <FileText size={18} className="text-primary" />
                                    <h6 className="modal-title fw-bold mb-0 text-truncate" style={{ maxWidth: '60vw' }}>
                                        {selectedPdf.name}
                                    </h6>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <a 
                                        href={selectedPdf.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                                        title="ახალ ჩანართში გახსნა"
                                    >
                                        <ExternalLink size={14} /> ახალ ფანჯარაში
                                    </a>
                                    <a 
                                        href={selectedPdf.url} 
                                        download 
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                        title="ჩამოტვირთვა"
                                    >
                                        <Download size={14} /> ჩამოტვირთვა
                                    </a>
                                    <button 
                                        type="button" 
                                        className="btn-close btn-close-white ms-2" 
                                        onClick={() => setSelectedPdf(null)}
                                    ></button>
                                </div>
                            </div>
                            <div className="modal-body p-0 bg-light" style={{ height: '75vh' }}>
                                <iframe 
                                    src={selectedPdf.url} 
                                    width="100%" 
                                    height="100%" 
                                    style={{ border: 'none' }} 
                                    title="PDF Document Viewer" 
                                />
                            </div>
                            <div className="modal-footer bg-white border-top py-2 px-3 d-flex justify-content-between">
                                <span className="text-muted smallest">
                                    OrbitroniX Municipal Document Previewer
                                </span>
                                <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setSelectedPdf(null)}>
                                    დახურვა
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminMunicipalityDetail;
